import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Video } from "lucide-react";
import { capFirst } from "@/lib/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const KINDS = ["Poser une question", "Expliquer mon besoin", "Demander une orientation", "Être recontactée", "Prendre rendez-vous en visio"];
const REASONS = ["Stress / charge mentale", "Sommeil", "Respiration", "Corps / mouvement", "Nutrition / habitudes", "Maternité", "Besoin de me recentrer", "Accompagnement personnalisé", "Je ne sais pas encore"];

export default function Echanger() {
  const [kind, setKind] = useState(null);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [durations, setDurations] = useState([]);
  const [duration, setDuration] = useState(null);
  // Règle : une fois une durée choisie, on ne peut que passer à une durée plus longue (jamais plus courte)
  const [minDuration, setMinDuration] = useState(null);
  const [slots, setSlots] = useState([]);
  const [reason, setReason] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [mine, setMine] = useState([]);
  const [sending, setSending] = useState(false);
  const [resalibUrl, setResalibUrl] = useState("");
  const [newsEmail, setNewsEmail] = useState("");
  const [reasons, setReasons] = useState(REASONS);
  const navigate = useNavigate();

  // Le bouton visio s'ouvre 10 min avant le rendez-vous et reste jusqu'à la fin (+15 min de marge)
  const visioOpen = (r) => {
    if (!r?.date || !r?.start) return false;
    const start = new Date(`${r.date}T${r.start}:00`);
    const end = new Date(start.getTime() + ((r.duration || 45) + 15) * 60000);
    const now = new Date();
    return now >= new Date(start.getTime() - 10 * 60000) && now <= end;
  };

  const token = localStorage.getItem("rav-token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    // Inscription obligatoire : la visio et le contact personnalisé rejoignent la fiche patient
    if (!token) {
      navigate("/connexion");
      return;
    }
    axios.get(`${API}/rdv/durations`).then(({ data }) => setDurations(data.durations)).catch(() => {});
    axios.get(`${API}/settings/public`).then(({ data }) => {
      setResalibUrl(data.settings.resalib_url || "");
      if (data.settings.visio_reasons?.length) setReasons(data.settings.visio_reasons);
    }).catch(() => {});
    if (token) {
      axios.get(`${API}/rdv/mine`, { headers }).then(({ data }) => setMine(data.rdv)).catch(() => {});
    }
  }, []);

  const chooseDuration = (value) => {
    const d = value ? parseInt(value, 10) : null;
    setDuration(d);
    if (minDuration === null && d) setMinDuration(d);
    setSlotId(null);
    setSlots([]);
    if (d) {
      axios.get(`${API}/rdv/slots`, { params: { duration: d } }).then(({ data }) => setSlots(data.slots)).catch(() => {});
    }
  };

  const chosenOffer = durations.find((d) => d.duration === duration);

  const subscribe = async () => {
    if (!newsEmail.includes("@")) {
      toast("Écrivez votre adresse e-mail.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email: newsEmail });
      toast(data.message);
      setNewsEmail("");
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue.");
    }
  };

  const sendMessage = async () => {
    if (!kind || !message.trim()) {
      toast("Choisissez le sujet et écrivez quelques mots.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { kind, message, email: email || null }, { headers });
      toast(data.message);
      setMessage("");
      setKind(null);
    } catch {
      toast("Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  const confirmRdv = async () => {
    if (!reason || !duration || !slotId) {
      toast("Choisissez un motif, une durée et un créneau.");
      return;
    }
    const [date, start] = slotId.split("|");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/rdv`, { date, start, duration, reason, email: email || null }, { headers });
      toast(`${data.message} — ${data.slot}`);
      setSlots((s) => s.filter((sl) => `${sl.date}|${sl.start}` !== slotId));
      setSlotId(null);
      setReason(null);
      if (token) {
        const { data: m } = await axios.get(`${API}/rdv/mine`, { headers });
        setMine(m.rdv);
      }
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <main className="rav-page" data-testid="echanger-page">
        <motion.img
          src="/logo-accueil.webp"
          alt="Respire Aime Vibre"
          className="rav-page-logo"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: EASE }}>
          <h1 className="rav-page-title" data-testid="echanger-title">Échanger avec Respire Aime Vibre</h1>
          <p className="rav-page-sub">Parfois, un Instant digital ne suffit pas. Nous sommes là.</p>

          {mine.length > 0 && (
            <div className="rav-admin-card" data-testid="my-rdv-card">
              <span className="rav-admin-card-icon"><Video size={22} strokeWidth={1.5} /></span>
              <span>
                <span className="rav-admin-card-value rav-rdv-label">Votre rendez-vous</span>
                <span className="rav-admin-card-label">{mine[0].slot_label}</span>
                {visioOpen(mine[0]) && (
                  <button type="button" className="rav-primary-button rav-visio-join" data-testid="rdv-join-visio" onClick={() => navigate(`/visio/${mine[0].id}`)}>
                    Rejoindre ma visio
                  </button>
                )}
              </span>
            </div>
          )}

          <h2 className="rav-section-title">Parler à Respire Aime Vibre</h2>
          <div className="rav-select-wrap">
            <select
              className="rav-input rav-select"
              data-testid="contact-kind-select"
              value={kind || ""}
              onChange={(e) => setKind(e.target.value || null)}
            >
              <option value="" disabled>
                Choisissez le sujet…
              </option>
              {KINDS.map((k) => (
                <option key={k} value={k} data-testid={`kind-${k}`}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <textarea
            className="rav-textarea"
            data-testid="contact-message"
            placeholder="Écrivez quelques mots, simplement…"
            value={message}
            onChange={(e) => setMessage(capFirst(e.target.value))}
            rows={4}
          />
          {!token && (
            <input
              type="email"
              className="rav-input"
              data-testid="contact-email"
              placeholder="Votre e-mail (pour vous répondre)"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
            />
          )}
          <button type="button" className="rav-primary-button" data-testid="contact-send" disabled={sending} onClick={sendMessage}>
            Envoyer en douceur
          </button>

          <h2 className="rav-section-title">Prendre rendez-vous</h2>
          {resalibUrl ? (
            <>
              <p className="rav-page-sub">
                Vous choisissez votre créneau directement sur l'agenda en ligne de Julie — simple et immédiat.
              </p>
              <a
                className="rav-primary-button rav-resalib-btn"
                data-testid="resalib-button"
                href={resalibUrl}
                target="_blank"
                rel="noreferrer"
              >
                Réserver mon créneau sur Resalib
              </a>
            </>
          ) : (
            <>
              <div className="rav-select-wrap">
                <select
                  className="rav-input rav-select"
                  data-testid="rdv-reason-select"
                  value={reason || ""}
                  onChange={(e) => setReason(e.target.value || null)}
                >
                  <option value="" disabled>
                    Choisissez votre motif…
                  </option>
                  {reasons.map((r) => (
                    <option key={r} value={r} data-testid={`reason-${r}`}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rav-select-wrap">
                <select
                  className="rav-input rav-select"
                  data-testid="rdv-duration-select"
                  value={duration || ""}
                  onChange={(e) => chooseDuration(e.target.value)}
                >
                  <option value="" disabled>
                    Choisissez votre durée…
                  </option>
                  {durations.map((d) => (
                    <option key={d.duration} value={d.duration} data-testid={`duration-${d.duration}`} disabled={minDuration !== null && d.duration < minDuration}>
                      {d.duration} min — {d.price} €
                    </option>
                  ))}
                </select>
              </div>
              {chosenOffer && (
                <p className="rav-rdv-recap" data-testid="rdv-recap">
                  Votre rendez-vous : {chosenOffer.duration} min — {chosenOffer.price} €
                </p>
              )}
              <div className="rav-select-wrap">
                <select
                  className="rav-input rav-select"
                  data-testid="rdv-slot-select"
                  value={slotId || ""}
                  disabled={!duration}
                  onChange={(e) => setSlotId(e.target.value || null)}
                >
                  <option value="" disabled>
                    {!duration
                      ? "Choisissez d'abord votre durée…"
                      : slots.length === 0
                        ? "Aucun créneau libre pour le moment…"
                        : "Choisissez votre créneau…"}
                  </option>
                  {slots.map((s) => (
                    <option key={`${s.date}|${s.start}`} value={`${s.date}|${s.start}`} data-testid={`slot-${s.date}-${s.start}`}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="rav-primary-button" data-testid="rdv-confirm" disabled={sending} onClick={confirmRdv}>
                Valider ma réservation et payer
              </button>
            </>
          )}

          <h2 className="rav-section-title rav-news-title">La lettre douce</h2>
          <p className="rav-page-sub rav-news-sub">Une fois par semaine, une respiration dans votre boîte mail. Rien de plus.</p>
          <div className="rav-news-box">
            <input
              type="email"
              className="rav-input"
              data-testid="newsletter-email"
              placeholder="Votre adresse e-mail"
              value={newsEmail}
              onChange={(e) => setNewsEmail(e.target.value.toLowerCase())}
            />
            <button type="button" className={`rav-secondary-button rav-session-btn${newsEmail ? " is-ready" : ""}`} data-testid="newsletter-submit" onClick={subscribe}>
              Recevoir la lettre
            </button>
          </div>
        </motion.div>
      </main>
    </>
  );
}
